import { useEffect, useState, type MouseEvent } from "react";
import { ProductCategoriesViewModel } from "../../viewmodels/primaryCategories/ProductCategoriesViewModel";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";

// Template Components
import DataManagementTemplate from "../../components/templates/DataManagementTemplate";
import MobileCardsTemplate from "../../components/templates/MobileCardsTemplate";

import SlideOver from "../../components/slideOver/SlideOver";
import EmptyModal from "../../components/modals/EmptyModal";
import type { DataColumn } from "../../components/templates/DataManagementTemplate";
import type { PaginatorPageChangeEvent } from "primereact/paginator";
import type { AppDispatch, RootState } from "../../index";
import type { ProductCategory } from "../../types/categories";

interface ProductCategoriesProps {
  logout: () => void;
}

function ProductCategories(props: ProductCategoriesProps) {
  const { logout } = props;
  const navigate = useNavigate();
  const {
    emptyModal,
    setEmptyModelOpen,
    getProductCategories,
    loading,
    primaryCategories,
    setSlideOver,
    slideOver,
    filterComponents,
    resetFilters,
    filters,
    getPrimaryCategoriesForDropdown,
    askDeleteCategory,
  } = ProductCategoriesViewModel(logout);

  const [currentPage, setCurrentPage] = useState(1);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    getProductCategories(null, currentPage, filters);
    getPrimaryCategoriesForDropdown();
  }, []);

  // Search handler
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
    getProductCategories(term || null, 1, filters);
  };

  // Pagination handler
  const handlePageChange = (event: PaginatorPageChangeEvent) => {
    const newPage = event.page + 1;
    setCurrentPage(newPage);
    setFirst(event.first);
    setRows(event.rows);
    getProductCategories(searchTerm || null, newPage, filters);
  };

  // Filter configuration
  const filterConfig = {
    dropdown: true,
    components: filterComponents,
    onReset: () => {
      setCurrentPage(1);
      setFirst(0);
      resetFilters();
    },
    onApply: () => {
      setCurrentPage(1);
      setFirst(0);
      getProductCategories(searchTerm || null, 1, filters);
    },
  };

  // Row click handler
  const handleRowClick = (category: ProductCategory) => {
    navigate(`/primary-categories/${category.id}`);
  };

  // Button configurations
  const headerButtons = [
    {
      label: "Add category",
      onClick: () => {
        navigate("/primary-categories/new");
      },
      className:
        "bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white focus:ring-primary-500",
    },
  ];

  // Column configurations for DataTable
  const columns: DataColumn<ProductCategory>[] = [
    {
      header: "Category Name",
      style: { width: "40%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">{rowData.name}</span>
      ),
    },
    {
      header: "Default",
      style: { width: "15%" },
      body: (rowData) => (
        <input
          type="checkbox"
          checked={Boolean(rowData.is_default)}
          readOnly
          aria-label={`${rowData.name} is${rowData.is_default ? "" : " not"} the default category`}
          className="h-4 w-4 rounded border-gray-300 text-primary-600"
        />
      ),
    },
    {
      header: "Sub Categories",
      style: { width: "25%" },
      body: (rowData) => (
        <span className="font-semibold apollo-subheading text-gray-900">
          {rowData.total_sub_categories}
        </span>
      ),
    },
    {
      header: "Actions",
      style: { width: "10%" },
      body: (rowData) => (
        <div className="flex items-center gap-2">
          <Link
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            to={`/primary-categories/${rowData.id}`}
          >
            View
          </Link>
          <button
            type="button"
            disabled={rowData.is_default}
            title={rowData.is_default ? "The default category cannot be deleted" : undefined}
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              askDeleteCategory(rowData);
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Mobile cards component
  const CategoriesMobileCards = ({ data }: { data: ProductCategory[] }) => (
    <MobileCardsTemplate
      data={data}
      onCardClick={(category) => navigate(`/primary-categories/${category.id}`)}
      onEdit={(category) => navigate(`/primary-categories/${category.id}`)}
      editRoute="/primary-categories"
      emptyMessage="No categories found"
      cardContent={(category) => (
        <div className="flex flex-col space-y-3">
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-h6 apollo-heading text-primary-600 mb-1">{category.name}</h3>
              {category.is_default && (
                <span className="inline-flex rounded-full bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700">
                  Default category
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      actionButtons={(category) => (
        <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
          <Link to={`/primary-categories/${category.id}`} className="flex-1">
            <button className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2">
              View
            </button>
          </Link>
          <button
            type="button"
            disabled={category.is_default}
            title={category.is_default ? "The default category cannot be deleted" : undefined}
            onClick={() => askDeleteCategory(category)}
            className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
          >
            Delete
          </button>
        </div>
      )}
    />
  );

  return (
    <div>
      <DataManagementTemplate
        title="Product Categories"
        description="Manage your product categories"
        headerButtons={headerButtons}
        filterConfig={filterConfig}
        columns={columns}
        data={primaryCategories.data || []}
        loading={loading}
        pagination={{
          first: first,
          rows: rows,
          total: primaryCategories.total || 0,
          template:
            "FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
          currentPageReportTemplate: "Showing {first} to {last} of {totalRecords} categories",
        }}
        onSearch={handleSearch}
        onPageChange={handlePageChange}
        onRowClick={handleRowClick}
        searchPlaceholder="Search by category"
        MobileCardsComponent={CategoriesMobileCards}
        emptyMessage="No categories found"
      />
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (_state: RootState) => {
  return {};
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProductCategories));
